import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Button from '../../../components/Button';
import Title from '../../../components/Title';
import Loading from '../../../components/Loading';
import useAdminTools from '../../../Hooks/useAdminTools';
import ToolForm from './Form';

const EditTool = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toolsData, tool, setTool, handleUpdateTool, handleAddToolItem, handleUpdateToolItem, handleDeleteToolItem, isLoading } = useAdminTools();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isActive = true;

    if (isActive) {
      const foundTool = toolsData.find((item) => item._id === id);
      if (foundTool) {
        setTool(foundTool);
      }
    }

    return () => {
      isActive = false;
    };
  }, [toolsData, id, setTool]);

  const handleItems = async (item, callback) => {
    await handleAddToolItem(id, item, (updatedTool, err) => {
      if (!err && updatedTool) {
        setTool(updatedTool);
      }
      if (callback) {
        callback();
      }
    });
  };

  const handleEditItem = async (item, callback) => {
    await handleUpdateToolItem(id, item._id, item, () => {
      if (callback) {
        callback();
      }
    });
  };

  const handleRemoveItem = async (itemId) => {
    await handleDeleteToolItem(id, itemId, (updatedTool, err) => {
      if (!err && updatedTool) {
        setTool(updatedTool);
      }
    });
  };

  const { title, description, requiredScore, type, items, relatedWorkbooks, status } = useMemo(() => {

    const workbookIds = tool?.relatedWorkbooks?.map(wb =>
      typeof wb === 'string' ? wb : wb._id
    ) || [];

    return {
      title: tool?.title,
      description: tool?.description,
      requiredScore: tool?.requiredScore,
      type: tool?.type,
      items: tool?.items || [],
      relatedWorkbooks: workbookIds,
      status: tool?.status
    };
  }, [tool]);

  if (isLoading) {
    return <Loading />;
  }

  if (!tool) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <h1 className="text-3xl font-bold">Tool Not Found</h1>
          <Button link="/tools/v2">
            <ArrowBack />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTool((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDragEnd = async (e) => {
    if (!e.destination) return;
    const tempData = Array.from(items);
    const [source_data] = tempData.splice(e.source.index, 1);
    tempData.splice(e.destination.index, 0, source_data);
    tempData.forEach((item, index) => (item.priority = index + 1));
    
    setTool((prev) => ({
      ...prev,
      items: tempData
    }));
  };

  return (
    <div className="w-full flex flex-col gap-8 p-8">
      <Title>Edit {title}</Title>
      <div className="bg-white rounded-lg p-8 shadow-sm">
        <ToolForm
          title={title}
          description={description}
          requiredScore={requiredScore}
          type={type}
          items={items}
          relatedWorkbooks={relatedWorkbooks}
          handleChange={handleChange}
          handleItems={handleItems}
          handleEditItem={handleEditItem}
          handleDeleteItem={handleRemoveItem}
          handleDragEnd={handleDragEnd}
        />
      </div>
      <div className="flex gap-4 justify-end">
        {status === 'publish' ? (
          <Button
            onClick={() => {
              setIsSubmitting(true);
              handleUpdateTool(() => {
                setIsSubmitting(false);
                navigate('/tools/v2');
              });
            }}
            disabled={isSubmitting}
          >
            Update Tool
          </Button>
        ) : (
          <>
            <Button
              outline
              onClick={() => {
                setIsSubmitting(true);
                setTool((prev) => ({ ...prev, status: 'draft' }));
                handleUpdateTool(() => {
                  setIsSubmitting(false);
                  navigate('/tools/v2');
                });
              }}
              disabled={isSubmitting}
            >
              Save as Draft
            </Button>
            <Button
              onClick={() => {
                setIsSubmitting(true);
                setTool((prev) => ({ ...prev, status: 'publish' }));
                handleUpdateTool(() => {
                  setIsSubmitting(false);
                  navigate('/tools/v2');
                });
              }}
              disabled={isSubmitting}
            >
              Publish
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default EditTool;

